import { SWServerSessionClient } from '@sparx/api/apis/sparx/messaging/server/v1/server.client';
import { AccessTokenProvider } from '@sparx/grpcweb';
import { getNow } from '@sparx/react-utils';
import queryString from 'query-string';
import { getCookie } from 'utils/cookies';

import { getTransport } from './transport';

// Load the URL from the global variable
const apiURL = window.__sparxweb.urls.api;
const tokenURL = window.__sparxweb.urls.token;
const csrfCookieName = window.__sparxweb.csrfCookieName;
const selectSchoolUrl = window.__sparxweb.urls.selectSchool;

const makeTokenFetcher = (onAccessTokenError: () => void) => async () => {
  const res = await fetch(tokenURL, {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (res.status === 401) {
    if (import.meta.env.DEV) {
      window.location.assign('https://www.test2.test.sparxmaths.uk/student');
    } else {
      onAccessTokenError();
    }
  }
  return res;
};

export const getAccessTokenProvider = (onAccessTokenError: () => void) => {
  const accessTokenProvider = new AccessTokenProvider(makeTokenFetcher(onAccessTokenError));
  accessTokenProvider.start().then(() => console.log('Access Token Provider initialised'));
  return accessTokenProvider;
};

const getCSRFCookie = () => getCookie(csrfCookieName);

export const getSelectSchoolUrl = (route?: string, forget?: boolean) => {
  const query = queryString.stringify({
    app: 'sparx_maths',
    user: 'student',
    route,
    ...(forget ? { forget: 1 } : {}),
  });
  return selectSchoolUrl + '?' + query;
};

export const redirectToSelectSchool = async (route?: string, forget?: boolean) => {
  window.location.replace(getSelectSchoolUrl(route, forget));
  return new Promise(() => {
    /*pass*/
  }); // never return
};

export const redirectToLogin = async (
  schoolID?: string,
  opts?: { forget?: boolean; path?: string },
) => {
  if (!schoolID) {
    schoolID = window.__sparxweb.schoolID;
  }
  const { selectschool, is, fg } = queryString.parse(window.location.search);
  const shouldSelectSchool = Boolean(selectschool?.toString()) || opts?.forget;
  const route = opts?.path ? `${window.location.origin}${opts.path}` : window.location.toString();
  const isQuery = is?.toString() ? { is: 'true' } : {};
  const fgQuery = fg?.toString() ? { fg: '1' } : {};

  if (!schoolID || shouldSelectSchool) {
    // Redirect to select school
    return redirectToSelectSchool(route, shouldSelectSchool);
  } else {
    // Redirect straight to auth endpoint
    const query = queryString.stringify({ school: schoolID, route, ...isQuery, ...fgQuery });
    window.location.replace(apiURL + '/oauth2/login?' + query);
  }
  return new Promise(() => {
    /*pass*/
  }); // never return
};

const createSessionLegacy = async () => {
  return (sessionPromise = fetch(apiURL + '/clientsession', {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'X-CSRF-Token': getCSRFCookie(),
      'Content-Type': 'application/json',
    },
  }).then(async response => {
    const { sessionID, serverNow } = await response.json();
    // Work out the timing offset between client and server:
    const serverNowDate = new Date(serverNow);
    const clientNow = getNow();
    const serverOffset = Math.round((serverNowDate.getTime() - clientNow.getTime()) / 1000);
    return { sessionID, serverOffset };
  }));
};

const createSessionSingleDomain = async () => {
  const accessTokenProvider = getAccessTokenProvider(() => {
    console.error('When setting up session, failed to get token, going back to login page');
    redirectToLogin();
  });

  const swserverSessionClient = new SWServerSessionClient(
    getTransport(apiURL, accessTokenProvider, { excludeSessionId: true }),
  );

  const getClientSessionPromise = async () => {
    try {
      const clientSessionResponse = await swserverSessionClient.clientSession({});
      if (!clientSessionResponse.response.sessionId || !clientSessionResponse.response.serverNow) {
        throw new Error('No session ID or server time returned');
      }

      // Work out the time difference between the client and server (serverOffset)
      const serverNowDate = new Date(clientSessionResponse.response.serverNow.seconds * 1000);
      const clientNow = getNow();
      const serverOffset = Math.round((serverNowDate.getTime() - clientNow.getTime()) / 1000);
      return {
        sessionID: clientSessionResponse.response.sessionId,
        serverOffset: serverOffset,
      };
    } catch (e) {
      console.error('Failed to create session, going back to login page', e);
      await redirectToLogin();
      return { sessionID: '', serverOffset: 0 };
    }
  };

  return getClientSessionPromise();
};

let sessionPromise: Promise<{ sessionID: string; serverOffset: number }> | undefined;
export const getSessionId = async () => {
  if (!sessionPromise) {
    if (window.__sparxweb.isSingleDomain) {
      sessionPromise = createSessionSingleDomain();
    } else {
      sessionPromise = createSessionLegacy();
    }
  }
  return sessionPromise;
};

export const makeLogoutRequest = async () => {
  if (window.__sparxweb.isSingleDomain) {
    return fetch(apiURL + '/oauth2/logout', {
      credentials: 'include',
    });
  } else {
    const { sessionID } = await getSessionId();
    return fetch(apiURL + '/logout', {
      method: 'POST',
      credentials: 'include',
      mode: 'cors',
      headers: {
        'X-CSRF-Token': getCSRFCookie(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ SessionID: sessionID }),
    });
  }
};
